import { makeAutoObservable, runInAction, toJS } from 'mobx'
import { cartMobx, orderMobx, productMobx, restaurantCenter, storeMobx } from 'restaurant'
import { restaurantCenter as mallCenter, cartMobx as mallCartMobx, mallProductMobx } from '@mobx/mall'
import { getHistoryNew, md5, showMsg } from '@utils'
import { ddayjs, getParams } from '@dt/fe-utils'
import { MktController, CashierController, MallController } from '@ctrls/index'
import { compact, filter, findIndex, forEach, sumBy, values } from '@dt/fe-utils/dist/lodash'
import MSDK from 'sdk/monitor'
import ProductAPI from '@api/product.api'
import { memberMobx } from '@mobxStore'

/** 领取优惠券默认图 */
export const defaultCouponBg = {
  pic: 'https://eshine-image.oss-cn-hangzhou.aliyuncs.com/20230825/ff04715891ba8eac.png',
  picWidth: 687,
  picHeight: 900
}

/** 点餐页广告 */
class AdvertStore {
  constructor () {
    makeAutoObservable(this)
    restaurantCenter.on(restaurantCenter.RESTAURANT_EVENT.MENU_END, (e) => {
      if (['product', 'mallProduct'].includes(this.adDetail.type)) {
        this.isMenuInit = true
        this.saveEventProdAdvert?.()
      }
    })
    restaurantCenter.on(restaurantCenter.RESTAURANT_EVENT.STORE_CHANGED, (info) => {
      MSDK?.setConfig({ storeId: info.storeId })
    })
    restaurantCenter.on(restaurantCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, (info) => {
      if (this.doProductFromAdvert?.type === 'product' && this.doProductFromAdvert?.key === info.cartItem?.productNo) {
        this.setProductInAdCache(info)
      }
    })
    mallCenter.on(mallCenter.RESTAURANT_EVENT.CART_ITEMS_CHANGED, (info) => {
      if (this.doProductFromAdvert?.type === 'mallProduct' && this.doProductFromAdvert?.key === info.cartItem?.productNo) {
        this.setProductInAdCache(info, true)
      }
    })
  }

  /** 首页广告触发餐品和商品，但是菜单商品未加载，需要保存标记，等餐品加载后再执行 */
  saveEventProdAdvert: any
  /** 菜单数据是否已经加载 */
  isMenuInit: boolean = false
  /** 当前广告位置 */
  adCaseType: TAdvertCaseType
  /** 当前广告图类型 */
  adType: TAdType
  /** 当前广告内容在第几页 */
  pageIndex: number
  /** 餐品广告可操作 */
  isChangeType: string = ''

  /** 保存广告内餐品在购物车的标记 */
  saveCartMap = {
    /**
     * 'productNo': {
     *    '图片地址': {
     *      cnt,
     *      adType: 'menu'
     *    },
     * }
     */
  }

  advertInit: boolean = false

  /** 是否展示广告  */
  showAdvert?: boolean

  /** 广告信息  */
  advertInfo = {} as {
    [key: string]: IAdvertDesignCase;
  }

  /** 默认高级设置 */
  detailConfig = {
    product: {
      img: [],
      bgColor: 'f4f4f4'
    },
    mallProduct: {
      img: [],
      bgColor: 'f4f4f4'
    },
  }

  /** 广告详情 */
  adDetail: TAdvert = {
    pic: '',
    type: 'product',
    intro: '',
    showDateType: 'always',
    showTimeType: 'allDay',
    showDate: [],
    showTime: [],
    products: [],
    mallProducts: [],
    hightConfig: {
      product: {
        img: [],
        bgColor: 'f4f4f4'
      },
      mallProduct: {
        img: [],
        bgColor: 'f4f4f4'
      },
    }
  }

  /** 标记广告内餐品来源 */
  doProductFromAdvert: { key: number; type: TAdType; adType: TAdvertCaseType; ad: TAdvert }

  /** 橱窗广告推荐餐品 */
  shopWindowRecommendInfo: {
    storeId: number;
    list: any[];
  } = {} as any

  /** 广告可展示内容 */
  get availableAdsMap () {
    // 当前时间字符串
    const nowDate = ddayjs().format('YYYYMMDD')
    const nowTime = ddayjs().format('HHmm')
    // 可用的广告
    const availableAdsMap = {}
    forEach(this.advertInfo, (item: IAdvertDesignCase, key) => {
      availableAdsMap[key] = item.ads?.filter((ad) => {
        let { showDate, showTime, showDateType, showTimeType, pic, wfName } = ad
        // 当前时间在广告设置的时间内 橱窗和信息流可能没有图片，需要单独判断
        return (pic || key === 'shopwindow' || (key === 'waterfall' && (pic || wfName))) && showDate.some(({ start, end }, index) => {
          const startDate = ddayjs(start).format('YYYYMMDD')
          const endDate = ddayjs(end).format('YYYYMMDD')
          const startTime = showTime[index].start.replace(':', '')
          const endTime = showTime[index].end.replace(':', '')
          return (showDateType === 'always' || (startDate <= nowDate && endDate >= nowDate)) && (showTimeType === 'allDay' || (startTime <= nowTime && endTime >= nowTime))
        })
      }) || []
    })
    return availableAdsMap
  }

  /** 根据门店展示 */
  get availableAdsGetStoreMap (): { [type: string]: any } {
    const mapObj = {}
    const storeId = storeMobx.info.storeId
    for (let key in this.availableAdsMap) {
      const advertList = this.availableAdsMap[key]
      mapObj[key] = {
        home: [],
        meals: []
      }
      forEach(advertList, (item: TAdvert) => {
        if (item.type === 'receiveCoupon' && !memberMobx.unacquiredCoupons.length) {
          return
        }
        if (item.showPage?.includes('home')) {
          mapObj[key].home.push(item)
        }
        // 点单页如果没有storeIds 说明是老数据 也需要展示
        if (item.showPage?.includes('meals') && (!item.storeIds || item.storeIds.includes(storeId))) {
          mapObj[key].meals.push(item)
        }
      })
    }
    return mapObj
  }

  /** 广告关联餐品 */
  get products () {
    return compact(this.adDetail.products.map(product => productMobx.noToViewProductMap[product.productNo])).filter(item => item.isNotSingleInCombo && item.isShowInMenu && !item.isOpenPrice && !item.disable && item.saleCnt !== 0)
  }

  /** 广告关联餐品 */
  get mallProducts () {
    // console.log(intersectionBy(this.adDetail.mallProducts, mallMobx.products, 'productId'))
    // return intersectionBy(this.adDetail.mallProducts, mallMobx.products, 'productId')
    // mallMobx.getProducts()
    return []
  }

  setProductInAdCache (info, isMall?: boolean) {
    const productNo = info.cartItem.productNo
    if (info.type === 'remove') {
      this.saveCartMap[productNo] = {}
      return
    }
    const cartItems = isMall ? mallCartMobx.cartItems : cartMobx.cartItems
    const adCaseType = this.doProductFromAdvert.adType
    const adId = md5(this.doProductFromAdvert.ad.pic)
    const productCnt = sumBy(filter(cartItems, (item: ICartItem) => item.productNo === productNo), (item: ICartItem) => item.cnt)
    !this.saveCartMap[productNo] && (this.saveCartMap[productNo] = {})
    const productInfo = this.saveCartMap[productNo]
    let total = 0
    forEach((Object.keys(productInfo) || []), (key) => {
      if (key !== adId) {
        total += productInfo[key].cnt
      }
    })
    const nowCnt = productCnt - total
    this.saveCartMap[productNo][adId] = {
      ...this.saveCartMap[productNo][adId],
      adType: adCaseType,
      cnt: nowCnt < 0 ? 0 : nowCnt,
      ad: this.doProductFromAdvert.ad
    }
  }

  /** 标记广告页操作餐品 */
  pickProductFromAdvert (key, type: TAdType, ad: TAdvert = this.adDetail, adType: TAdvertCaseType = this.adCaseType) {
    this.doProductFromAdvert = { key, type, adType, ad }
  }

  clearProductFromAdvert () {
    this.doProductFromAdvert = null
  }

  clearSaveCart () {
    this.saveCartMap = {}
  }

  /** 设置显示 */
  setShowAdvert (showAdvert: boolean) {
    this.showAdvert = showAdvert
  }

  /** 设置广告信息 */
  setAdverList (brandAdvertList: any[], storeAdvertList: any[], manualSwitch: boolean) {
    this.setShowAdvert(manualSwitch)
    let advertHome: any = {}
    let advertMenu: any = {}
    let advertFinish: any = {}
    let advertBuyTogether: any = {}
    let advertOpenScreen: any = {}
    let advertPopUps: any = {}
    let advertShopwindow: any = {}
    let advertWaterfall: any = {}
    try {
      brandAdvertList?.map((item) => {
        const advertContent = item.content ? JSON.parse(item.content) : {}
        if (advertContent.type === 'home') advertHome = advertContent || {}
        if (advertContent.type === 'openScreen') advertOpenScreen = advertContent || {}
        if (advertContent.type === 'popUps') advertPopUps = advertContent || {}
      })
      storeAdvertList?.map((item) => {
        const advertContent = item.content ? JSON.parse(item.content) : {}
        if (advertContent.type === 'menu' && !Object.keys(advertMenu).length) { advertMenu = advertContent || {} }
        if (advertContent.type === 'finish' && !Object.keys(advertFinish).length) { advertFinish = advertContent || {} }
        if (advertContent.type === 'buyTogether' && !Object.keys(advertBuyTogether).length) { advertBuyTogether = advertContent || {} }
        if (advertContent.type === 'shopwindow' && !Object.keys(advertShopwindow).length) { advertShopwindow = advertContent || {} }
        if (advertContent.type === 'waterfall' && !Object.keys(advertWaterfall).length) { advertWaterfall = advertContent || {} }
      })

      this.advertInfo = { home: advertHome, menu: advertMenu, finish: advertFinish, openScreen: advertOpenScreen, buyTogether: advertBuyTogether, popUps: advertPopUps, shopwindow: advertShopwindow, waterfall: advertWaterfall }
      setTimeout(() => {
        this.setShowAdvert(true)
        this.setAdvertInit()
      }, 200)
      // 橱窗广告推荐
      setTimeout(() => {
        this.getShopWindowRecommends()
      }, 1500)
    } catch (error) {
      this.setShowAdvert(true)
    }
  }

  /** 小程序过来的广告标识，需要初始化 */
  setAdvertInit () {
    if (Config.targetType && Config.targetType.indexOf('advert@') > -1 && !this.advertInit) {
      const targetData = Config.targetType.split('@')
      const adCaseType = targetData[1] as TAdvertCaseType
      const pageIndex = targetData[2]
      const ad = this.advertInfo?.[adCaseType].ads?.[pageIndex]
      ad && this.pick(this.advertInfo[adCaseType].ads[pageIndex], adCaseType)
    }
  }

  /** 展示某个广告详情 */
  pick (info: TAdvert, adType: TAdvertCaseType) {
    this.adDetail = info
    this.adCaseType = adType
    this.advertInit = true
    this.isChangeType = info.type
    this.pageIndex = findIndex(this.advertInfo[this.adCaseType].ads, (item: TAdvert) => item.pic === info.pic)
  }

  /** 前往广告列表需要判断是否有商品 */
  goToProductAdvert (products, type = 'product') {
    const history = getHistoryNew()
    if (type === 'product') {
      const _products = products.map(product => productMobx.noToViewProductMap[product.productNo]).filter(item => item?.isNotSingleInCombo && item.isShowInMenu && !item.disable && item.saleCnt !== 0 && !item.isOpenPrice)
      if (_products.length === 1) {
        this.pickProductFromAdvert(_products[0].no, 'product')
        CashierController.showProductDetail(_products[0])
      } else if (_products.length > 0 && !history?.location?.pathname.includes('advert')) {
        history.replace('/advert')
      } else if (!_products.length) {
        showMsg(i18n.t('store.noProdInStoreTip'), 2000)
        setTimeout(() => {
          MktController.goBack(history)
        }, 2000)
      }
      return _products
    } else if (type === 'mallProduct') {
      const _products = products
      if (_products.length === 1) {
        this.pickProductFromAdvert(_products[0].no, 'mallProduct')
        MallController.goToProduct(_products[0])
      } else if (_products.length > 0 && !history?.location?.pathname.includes('advert')) {
        history.replace('/advert')
      } else if (!_products.length) {
        showMsg(i18n.t('store.noMallProdInStoreTip'), 2000)
        setTimeout(() => {
          MktController.goBack(history)
        }, 2000)
      }
      return _products
    }
  }

  /** 前往餐品广告 */
  homeToMenuAdvert () {
    const { tt: targetType = '' } = getParams()
    const history = getHistoryNew()
    if (targetType === 'advert' && this.isChangeType === 'product') {
      this.isChangeType = ''
      this.saveEventProdAdvert = null
      setTimeout(() => history.replace('/advert'), 500)
    }
  }

  /** 前往餐品广告验证是否可打开，需要依赖当前门店的菜单加载完成 */
  checkToMenuAdvert = () => {
    // 如果菜单已经加载，就直接执行，如果菜单未加载，就先存储事件，等菜单加载完成后执行
    if (this.isMenuInit) {
      this.homeToMenuAdvert()
    } else {
      this.saveEventProdAdvert = this.homeToMenuAdvert
    }
  }

  /** 计算下单餐品是广告来源并且上报 */
  traceCartProductsFromAdvert (orderInfo) {
    orderMobx.viewDetailList
    const propertysMap = {} // 上报内容按照广告拆分
    orderMobx.viewDetailList.forEach((item) => {
      const cnt = item.cnt // 总数量
      const unitOffPrice = (item.singleOffPrice || 0) / cnt
      const unitPrice = item.unitPrice // 单价
      const productInfo = this.saveCartMap[item.productNo]
      let _cnt = cnt // 标记数量
      forEach((Object.keys(productInfo) || []), (key) => {
        let page = findIndex(this.advertInfo[productInfo[key]?.adType].ads, (item: TAdvert) => item.pic === productInfo?.[key].ad.pic)
        !propertysMap[key] && (propertysMap[key] = {
          event: 'ad_order',
          event_time: ddayjs().format('YYYY-MM-DD HH:mm:ss'), // 事件发生时间
          page_name: i18n.t('pageName.checkout'), // 页面名
          content: {
            ad_type: productInfo[key].adType,
            ad_page: page >= 0 ? page : 0,
            ad_content: productInfo[key].ad.pic,
            target_type: productInfo[key].ad.type,
            cnt: 0,
            target_amount: 0,
            total_amount: orderInfo.paidFee
          }
        })
        const itemCnt = productInfo[key].cnt - propertysMap[key].content.cnt
        if (_cnt > 0 && itemCnt > 0) {
          const savePrice = (_cnt > itemCnt ? itemCnt : _cnt) * (unitPrice - unitOffPrice)
          const saveCnt = _cnt > itemCnt ? itemCnt : _cnt
          _cnt = _cnt > saveCnt ? _cnt - saveCnt : 0
          propertysMap[key].content.cnt = propertysMap[key].content.cnt + saveCnt
          propertysMap[key].content.target_amount = propertysMap[key].content.target_amount + savePrice
        }
      })
    })
    const productList = values(propertysMap)
    if (productList.length) {
      this.clearSaveCart()
      MSDK.trackOrder(productList, orderInfo.ordersId)
    }
  }

  /** 判断当前会员相关下单类型是否可上报 */
  checkAdTypeCanntUpload (orderInfo) {
    const adDetail = this.adDetail
    const adType = adDetail.type
    const { eatType } = orderInfo
    // 团购商城
    if (eatType === 'redeemCouponByMoney' && adType === 'groupBuy') {
      return false
    }
    // 礼品卡
    if (['giftcard', 'giftcardCz'].includes(eatType) && adType === 'giftCard') {
      return false
    }
    // 会员卡
    if (['memberCz', 'memberCardBuy', 'memberCardRenew'].includes(eatType) && adType === 'memberCard') {
      return false
    }
    if (eatType === adType) {
      return false
    }
    // if (eat)
    return true
  }

  /** 下单是广告来源并且上报 非餐品广告 */
  traceBuyAdvert (orderInfo) {
    if (this.checkAdTypeCanntUpload(orderInfo)) return
    const adDetail = this.adDetail
    const cnt = sumBy(orderInfo.detailList || [], (item: any) => {
      if (!['giftcard', 'giftcardCz', 'memberCz', 'memberCardBuy', 'memberCardRenew', 'redeemCouponByMoney'].includes(item.itemType)) {
        return 0
      }
      return item.cnt
    })
    MSDK.trackOrder({
      event: 'ad_order',
      event_time: ddayjs().format('YYYY-MM-DD HH:mm:ss'), // 事件发生时间
      page_name: i18n.t(`advert.${adDetail.type}${adDetail.type === 'groupBuy' ? '2' : ''}`), // 页面名
      content: {
        ad_type: this.adCaseType,
        ad_page: this.pageIndex,
        ad_content: adDetail.pic,
        target_type: adDetail.type,
        cnt: cnt || orderInfo.cnt || 1,
        target_amount: orderInfo.paidFee,
        total_amount: orderInfo.paidFee
      }
    }, orderInfo.ordersId)
  }

  /** 橱窗餐品 */
  async getShopWindowRecommends () {
    const advert = this.advertInfo?.shopwindow
    const { queueType = 'auto', queueData } = advert?.extInfo || {}
    if (this.shopWindowRecommendInfo.storeId === storeMobx.info.storeId) {
      return
    }
    if (advert && queueType === 'auto' && queueData?.auto) {
      const itemData = queueData?.auto
      // 当前时间字符串
      const showTime = itemData.showTime
      const showDateType = itemData.showDateType
      const showTimeType = itemData.showTimeType
      const nowDate = ddayjs().format('YYYYMMDD')
      const nowTime = ddayjs().format('HHmm')
      const inDate = itemData.showDate.some(({ start, end }, index) => {
        const startDate = ddayjs(start).format('YYYYMMDD')
        const endDate = ddayjs(end).format('YYYYMMDD')
        const startTime = showTime[index].start.replace(':', '')
        const endTime = showTime[index].end.replace(':', '')
        return (showDateType === 'always' || (startDate <= nowDate && endDate >= nowDate)) && (showTimeType === 'allDay' || (startTime <= nowTime && endTime >= nowTime))
      })
      if (inDate) {
        this.shopWindowRecommendInfo.storeId = storeMobx.info.storeId
        const res = await ProductAPI.getRecommendCart(true)
        if (res?.recommend_products?.length) {
          const list = res?.recommend_products.map((item) => ({
            productNo: item,
            ad: {
              type: 'product',
              products: [{ productNo: item.productNo }],
              ...advert.extInfo.queueData.auto || {}
            }
          }))
          runInAction(() => {
            this.shopWindowRecommendInfo.list = list
          })
        }
      }
    }
  }
}

export default new AdvertStore()
